/**
 * BST Blog Grid — initial article limit, view all, infinite scroll.
 */
(function () {
  'use strict';

  const GRID_SELECTOR = '[data-bst-blog-grid]';
  const SENTINEL_SELECTOR = '[data-bst-blog-load-sentinel]';
  const VIEW_ALL_SELECTOR = '[data-bst-blog-view-all]';
  const ARTICLE_SELECTOR = '[data-bst-blog-article]';

  function getScrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  class BstBlogGrid extends HTMLElement {
    constructor() {
      super();
      this.grid = null;
      this.sentinel = null;
      this.viewAllButton = null;
      this.observer = null;
      this.loading = false;
      this.sectionId = '';
      this.nextUrl = '';

      this.onViewAllClick = this.onViewAllClick.bind(this);
    }

    connectedCallback() {
      this.grid = this.querySelector(GRID_SELECTOR);
      this.sentinel = this.querySelector(SENTINEL_SELECTOR);
      this.viewAllButton = this.querySelector(VIEW_ALL_SELECTOR);
      this.sectionId = this.dataset.sectionId || '';
      this.nextUrl = this.dataset.nextUrl || '';

      if (!this.grid) return;

      if (this.viewAllButton) {
        this.viewAllButton.addEventListener('click', this.onViewAllClick);
      }

      if (this.dataset.expanded === 'true') {
        this.loadAllPages();
      }
    }

    disconnectedCallback() {
      if (this.viewAllButton) {
        this.viewAllButton.removeEventListener('click', this.onViewAllClick);
      }

      this.observer?.disconnect();
    }

    onViewAllClick(event) {
      event.preventDefault();
      this.expand();
    }

    async expand() {
      if (this.dataset.expanded === 'true') return;

      this.dataset.expanded = 'true';

      if (this.viewAllButton) {
        this.viewAllButton.hidden = true;
      }

      this.revealLoadedArticles();
      this.notifyArticlesChanged();
      await this.loadAllPages();
      this.scrollToNewArticles();
    }

    getInitialCount() {
      const value = parseInt(this.dataset.initialCount || '5', 10);
      return Number.isNaN(value) ? 5 : value;
    }

    scrollToNewArticles() {
      if (!this.grid) return;

      const initialCount = this.getInitialCount();
      const visibleArticles = [...this.grid.querySelectorAll(ARTICLE_SELECTOR)].filter(
        (item) => !item.hidden
      );

      if (visibleArticles.length <= initialCount) return;

      const target = visibleArticles[initialCount];
      if (!target) return;

      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
      });
    }

    revealLoadedArticles() {
      if (!this.grid) return;

      this.grid.querySelectorAll(ARTICLE_SELECTOR).forEach((item) => {
        item.hidden = false;
      });
    }

    enableInfiniteScroll() {
      if (!this.sentinel || !this.nextUrl || this.observer) return;

      this.sentinel.hidden = false;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage();
          }
        },
        { rootMargin: '240px 0px' }
      );

      this.observer.observe(this.sentinel);
    }

    async loadAllPages() {
      while (this.nextUrl) {
        await this.loadNextPage();
      }

      this.observer?.disconnect();
      this.observer = null;

      if (this.sentinel) {
        this.sentinel.hidden = true;
      }
    }

    async loadNextPage() {
      if (!this.nextUrl || this.loading) return;

      this.loading = true;
      this.sentinel?.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error('Failed to load blog articles');

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = parsed.querySelector(GRID_SELECTOR);

        if (!nextGrid) throw new Error('Missing blog grid in response');

        const newItems = [...nextGrid.querySelectorAll(ARTICLE_SELECTOR)];
        newItems.forEach((item) => {
          item.hidden = false;
          this.grid.appendChild(item);
        });

        const nextComponent = parsed.querySelector('bst-blog-grid');
        this.nextUrl = nextComponent?.dataset.nextUrl || '';
        this.dataset.nextUrl = this.nextUrl;

        this.notifyArticlesChanged();
      } catch (error) {
        console.error('BST blog infinite scroll failed', error);
        this.nextUrl = '';
        this.dataset.nextUrl = '';
      } finally {
        this.loading = false;
        this.sentinel?.removeAttribute('aria-busy');
      }
    }

    notifyArticlesChanged() {
      document.dispatchEvent(
        new CustomEvent('bst-blog:articles-changed', {
          detail: { grid: this.grid },
        })
      );
    }
  }

  if (!customElements.get('bst-blog-grid')) {
    customElements.define('bst-blog-grid', BstBlogGrid);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(VIEW_ALL_SELECTOR);
    if (!button || button.hidden) return;

    const component = button.closest('bst-blog-grid');
    if (component && typeof component.expand === 'function') {
      event.preventDefault();
      component.expand();
    }
  });
})();
